import { Menu as AntdMenu, ConfigProvider } from 'antd'
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb'
import type {
  ItemType,
  MenuItemGroupType,
  MenuItemType as OrgMenuItemType,
} from 'antd/es/menu/interface'
import React, { useCallback, useEffect, useState } from 'react'
import { Link, matchPath, useLocation } from 'react-router-dom'
import { get } from '@micun/web-components'
import { useLayout } from '.'
import { genMenuStyle } from './menu.styles'

import { ReactComponent as IconMenuUp } from 'assets/svg/icon-menu-up.svg'
import { ReactComponent as IconMenuRight } from 'assets/svg/icon-menu-right.svg'
import { ReactComponent as IconMenuDown } from 'assets/svg/icon-menu-down.svg'

import { ReactComponent as 主档管理 } from 'assets/svg/menu-icons/主档管理.svg'
import { ReactComponent as 财务系统 } from 'assets/svg/menu-icons/财务系统.svg'
import { ReactComponent as 待办事项 } from 'assets/svg/menu-icons/待办事项.svg'
import { ReactComponent as 订单管理 } from 'assets/svg/menu-icons/订单管理.svg'
import { ReactComponent as 合同管理 } from 'assets/svg/menu-icons/合同管理.svg'
import { ReactComponent as 活动管理 } from 'assets/svg/menu-icons/活动管理.svg'
import { ReactComponent as 开店系统 } from 'assets/svg/menu-icons/开店系统.svg'
import { ReactComponent as 流程管理 } from 'assets/svg/menu-icons/流程管理.svg'
import { ReactComponent as 商品管理 } from 'assets/svg/menu-icons/商品管理.svg'
import { ReactComponent as 通用设置 } from 'assets/svg/menu-icons/通用设置.svg'
import { ReactComponent as 人事管理 } from 'assets/svg/menu-icons/人事管理.svg'
import { ReactComponent as 任务管理 } from 'assets/svg/menu-icons/任务管理.svg'
import { ReactComponent as 门店经营 } from 'assets/svg/menu-icons/门店经营.svg'
import { ReactComponent as 运营管理 } from 'assets/svg/menu-icons/运营管理.svg'
import { ReactComponent as 人员管理 } from 'assets/svg/menu-icons/人员管理.svg'
import { useCssInJs } from 'hooks/useCssInJs'
import classNames from 'classnames'

type IconType = React.FunctionComponent<
  React.SVGProps<SVGSVGElement> & {
    title?: string
  }
>
const ICONS: AnyObject<IconType> = {
  主档管理,
  财务系统,
  待办事项,
  订单管理,
  合同管理,
  活动管理,
  开店系统,
  流程管理,
  商品管理,
  通用设置,
  人事管理,
  任务管理,
  门店经营,
  运营管理,
  人员管理,
}

type OriginMenuItemType = ItemType<OrgMenuItemType>

type MenuItemType = OriginMenuItemType & {
  parent?: MenuItemType
  children?: MenuItemType[]
  title?: string
}

const MENU_ICON = {
  UP: <IconMenuUp className='ant-menu-submenu-arrow' />,
  RIGHT: <IconMenuRight className='ant-menu-submenu-arrow' />,
  DOWN: <IconMenuDown className='ant-menu-submenu-arrow' />,
}

export function Menu() {
  const prefix = 'menu'
  const { wrapSSR, hashId } = useCssInJs({
    prefix,
    styleFun: genMenuStyle,
  })
  const [menus, setMenus] = useState<MenuItemType[]>([])
  const { config, collapsed, setBaseBreadcrumb } = useLayout()
  const location = useLocation()

  const { pathname } = location

  // const matchPath = useMatch(pathname)

  const [selectedKeys, setSelectedKeys] = useState<string[]>([pathname])
  const [openKeys, setOpenKeys] = useState<string[]>([])

  useEffect(() => {
    if (menus && menus.length) {
      const findMatchedMenus = (
        menus: MenuItemType[],
        pathname: string,
        // opened: MenuItemType[] = [],
        selected: MenuItemType[] = []
      ) => {
        menus.forEach((menu) => {
          const matched = matchPath(
            {
              path: menu.key as string,
              caseSensitive: false,
              end: false,
            },
            pathname
          )

          const hasChildren = !!(menu as MenuItemGroupType).children

          findMatchedMenus(
            menu.children || [],
            pathname,
            // opened,
            selected
          )

          if (matched && !hasChildren) {
            selected.push(menu)
          }
        })

        return selected
      }

      const selectedMenus = findMatchedMenus(menus, pathname)

      // console.log('selectedMenus', selectedMenus)

      const nextOpenKeys: string[] = []
      const nextSelectedKeys: string[] = []
      const breadcrumbs: BreadcrumbItemType[] = []
      selectedMenus.forEach((menu) => {
        let parent = menu.parent
        while (parent) {
          breadcrumbs.push({
            key: parent.key,
            title: parent.title,
          })
          if (parent.key) {
            nextOpenKeys.push(String(parent.key))
          }
          parent = parent.parent
        }

        breadcrumbs.push({
          key: menu.key,
          title: menu.title,
        })

        if (menu.key) {
          nextSelectedKeys.push(String(menu.key))
        }
      })

      // console.log('openKeys', openKeys, nextOpenKeys)
      // console.log('selectedKeys', selectedKeys, nextSelectedKeys)
      setBaseBreadcrumb(breadcrumbs)
      if (!collapsed) {
        // collapsed 状态默认页面加载后不弹出子菜单 ↓
        setOpenKeys((prev) => Array.from(new Set([...prev, ...nextOpenKeys])))
      }
      setSelectedKeys(nextSelectedKeys)
    }
  }, [pathname, menus, setBaseBreadcrumb, collapsed])

  const updateMenu = useCallback(
    (tree: api.rbac.user.navigation.PermissionTreeVo[]) => {
      const transformItem = (
        node: api.rbac.user.navigation.PermissionTreeVo,
        parent?: MenuItemType
      ): MenuItemType => {
        let pageConfig
        try {
          pageConfig = JSON.parse(node.pageConfig)
        } catch (e) {}

        const Icon = ICONS[pageConfig?.icon as string]

        // TODO ICON 配置
        const hasChildren = node.subPermissions && node.subPermissions.length
        const current: MenuItemType = {
          label: hasChildren ? (
            node.permissionName
          ) : (
            <Link
              to={node.uri}
              target={/^http/.test(node.uri) ? '_blank' : '_self'}
            >
              {node.permissionName}
            </Link>
          ),
          icon: Icon ? <Icon /> : null,
          title: node.permissionName,
          key: node.uri,
          children: hasChildren ? [] : undefined,
          className: parent ? undefined : 'submenu-root',
          popupClassName: classNames(prefix, hashId),
          parent,
        }

        if (node.subPermissions && node.subPermissions.length) {
          current.children?.push(
            ...node.subPermissions.map((sub) => transformItem(sub, current))
          )
        }

        return current
      }
      const items = tree?.map((item) => transformItem(item))

      setMenus(items)
    },
    [hashId]
  )

  const loadMenu = useCallback(async () => {
    const result: any = await get({
      url: '/rbac/user/permissionTree',
      query: {
        appCode: config.appCode,
        uri: 'cbs_web',
        showTypes: 'NAVIGATION',
        filterNoPermission: true,
      },
    })
    if (result.code === 0 || result.code === 200) {
      // updateMenu(result.data)
      // console.log('menu', result.data)
      updateMenu(result.data[0]?.subPermissions)
    }
  }, [config.appCode, updateMenu])

  useEffect(() => {
    loadMenu()
  }, [loadMenu])

  return wrapSSR(
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            itemBg: 'transparent',
            itemHeight: 38,
          },
        },
      }}
    >
      <AntdMenu
        mode='inline'
        inlineIndent={8}
        className='!pb-8'
        // theme='dark'
        expandIcon={({ isOpen }) => {
          if (collapsed) {
            return MENU_ICON.RIGHT
          }
          if (isOpen) {
            return MENU_ICON.UP
          }
          return MENU_ICON.DOWN
        }}
        items={menus}
        inlineCollapsed={collapsed}
        openKeys={openKeys}
        selectedKeys={selectedKeys}
        onOpenChange={setOpenKeys}
      />
    </ConfigProvider>
  )
}
